/**
 * Lifts the mapped "main guest" cells off the built rows and into the job's
 * `partyKeys` map, keyed by row index — the shape `dedupeDecisions` already
 * uses, and for the same reason: it is a per-row instruction to the producer,
 * not a field of the guest.
 *
 * Why the value cannot simply ride on the row:
 *  - `Guest.mainGuest` is an ObjectId on the backend DTO, with a JSON-B
 *    adapter. A cell holding an email address would fail at BINDING time and
 *    400 the entire job, before a single row could be explained to the
 *    operator.
 *  - Anything else added to the Guest entity is persisted: `DocumentMapper`
 *    writes every non-null, non-static field and honours no transient marker.
 *
 * The producer binds these keys to the row objects before dedupe runs, so the
 * index is only ever read while the list is still whole (see
 * ImportPartyResolver.bindKeys).
 */
import { partyKeyValue } from "@/lib/utils/partyKeyValue";
import { parseArrivalNoticeCell } from "../arrivalNotice/arrivalNoticeCells";

export type PartyKeyMap = Record<string, string>;

type RowLike = Record<string, unknown>;

/**
 * Removes `column` from every row and returns the non-blank values by index.
 * Mutates the rows: the value must not reach the wire in that field.
 */
function liftColumn(rows: RowLike[], column: string): PartyKeyMap {
  return liftColumnWith(rows, column, (raw) => {
    // Trimmed — and, when the key is an address, cleaned like the e-mail
    // column it has to equal (see partyKeyValue).
    const value = partyKeyValue(raw);
    return value === "" ? undefined : value;
  });
}

/** `liftColumn` with the cell's reading supplied: `undefined` keeps no entry. */
function liftColumnWith<T>(
  rows: RowLike[],
  column: string,
  read: (raw: string) => T | undefined,
): Record<string, T> {
  const out: Record<string, T> = {};
  rows.forEach((row, index) => {
    const raw = row[column];
    // Always delete, even when blank: an empty string in an ObjectId field is
    // its own binding failure, and an unknown key on a guest row is a claim
    // the backend must never have to interpret.
    delete row[column];
    if (typeof raw !== "string") return;
    const value = read(raw);
    if (value === undefined) return;
    out[String(index)] = value;
  });
  return out;
}

/** The party links: the "main guest" cells (`mainGuest`), lifted by row. */
export function extractPartyKeys(rows: RowLike[]): PartyKeyMap {
  return liftColumn(rows, "mainGuest");
}

/**
 * The party aliases: the label a main-guest row gives itself (`partyAlias`),
 * lifted by row into `partyAliases`. Same contract as the keys — the producer
 * binds both maps to the row objects before dedupe, so the indices must be
 * those of the array that is actually submitted.
 */
export function extractPartyAliases(rows: RowLike[]): PartyKeyMap {
  return liftColumn(rows, "partyAlias");
}

/**
 * W4 Hop B (N29): the arrival-notice cells (`arrivalNotice`), lifted by row —
 * the addresses of each VALID cell (a cell with a bad address or more than
 * the cap is dropped whole, the checker has said so). Same index contract as
 * the party keys: PImportCore turns the addresses into contact ids and sends
 * `arrivalNoticeLinks` keyed by these indices; the addresses never leave.
 */
export function extractArrivalNoticeCells(rows: RowLike[]): Record<string, string[]> {
  return liftColumnWith(rows, "arrivalNotice", (raw) => {
    const cell = parseArrivalNoticeCell(raw);
    return cell.kind === "ok" ? cell.emails : undefined;
  });
}
